import React, { useEffect, useState, useMemo } from 'react';
import {
    Container,
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
    Chip,
    FormControlLabel,
    Switch,
    Tooltip,
    TextField,
    Grid,
} from '@mui/material';
import {
    GetApp as ExportIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { db } from '../firebase/config';
import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    startAfter,
} from 'firebase/firestore';

export default function Scoreboard() {
    // State for forms
    const [forms, setForms] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);

    // State for submissions
    const [submissions, setSubmissions] = useState([]);
    const [filteredSubmissions, setFilteredSubmissions] = useState([]);

    // State for UI
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showQuestionScores, setShowQuestionScores] = useState(false);
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');

    // ===== FETCH ALL FORMS =====
    useEffect(() => {
        const fetchForms = async () => {
            try {
                setLoading(true);
                const formsRef = collection(db, 'forms');
                const formsSnap = await getDocs(formsRef);

                if (formsSnap.empty) {
                    setError('No forms available');
                    setLoading(false);
                    return;
                }

                const formsList = formsSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setForms(formsList);

                // Auto-select first form
                if (!selectedFormId && formsList.length > 0) {
                    setSelectedFormId(formsList.id);
                    setSelectedForm(formsList);
                }
            } catch (err) {
                console.error('Error fetching forms:', err);
                setError('Failed to load forms');
            } finally {
                setLoading(false);
            }
        };

        fetchForms();
    }, []);

    // ===== HANDLE FORM SELECTION =====
    const handleFormChange = (event) => {
        const formId = event.target.value;
        setSelectedFormId(formId);

        const form = forms.find((f) => f.id === formId);
        setSelectedForm(form);
    };

    // ===== FETCH SUBMISSIONS FOR SELECTED FORM =====
    useEffect(() => {
        if (!selectedFormId) return;

        const fetchSubmissions = async () => {
            try {
                setLoading(true);
                const submissionsRef = collection(db, 'submissions');
                const q = query(
                    submissionsRef,
                    where('formId', '==', selectedFormId),
                    orderBy('submittedAt', 'desc')
                );

                const snap = await getDocs(q);
                const subsList = snap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setSubmissions(subsList);
                setFilteredSubmissions(subsList);
                setError('');
            } catch (err) {
                console.error('Error fetching submissions:', err);
                setError('Failed to load submissions');
            } finally {
                setLoading(false);
            }
        };

        fetchSubmissions();
    }, [selectedFormId]);

    // ===== APPLY DATE FILTERS =====
    useEffect(() => {
        let filtered = submissions;

        if (dateFromFilter) {
            const fromDate = new Date(dateFromFilter);
            filtered = filtered.filter((sub) => {
                const subDate = sub.submittedAt?.toDate?.() || new Date(0);
                return subDate >= fromDate;
            });
        }

        if (dateToFilter) {
            const toDate = new Date(dateToFilter);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter((sub) => {
                const subDate = sub.submittedAt?.toDate?.() || new Date(0);
                return subDate <= toDate;
            });
        }

        setFilteredSubmissions(filtered);
    }, [submissions, dateFromFilter, dateToFilter]);

    // ===== BUILD DYNAMIC COLUMNS =====
    const columns = useMemo(() => {
        if (!selectedForm) return [];

        const isEvaluation = selectedForm.formType === 'evaluation';
        const isInputDetail = selectedForm.formType === 'input-detail';
        const isFeedback = selectedForm.formType === 'feedback';

        // Base columns
        const base = [
            { key: 'studentName', label: 'Startup / Participant Name', width: 180 },
            { key: 'submittedAt', label: 'Submission Date & Time', width: 160 },
        ];

        // Question/Field columns
        let questionCols = [];

        if (isEvaluation || isFeedback) {
            questionCols = (selectedForm.questions || []).map((q, idx) => ({
                key: q.id || `q${idx}`,
                label: q.text,
                questionMeta: q,
                width: 150,
            }));
        }

        if (isInputDetail) {
            questionCols = (selectedForm.fields || []).map((f, idx) => ({
                key: f.id || `f${idx}`,
                label: f.label,
                fieldMeta: f,
                width: 150,
            }));
        }

        // Status column
        const tail = [{ key: 'status', label: 'Status', width: 100 }];

        let allCols = [...base, ...questionCols, ...tail];

        // Add scoring columns for evaluation forms
        if (isEvaluation) {
            if (showQuestionScores) {
                const scoreCols = (selectedForm.questions || []).map((q, idx) => ({
                    key: `score_${q.id || `q${idx}`}`,
                    label: `Score: ${q.text}`,
                    width: 120,
                }));
                allCols = [...allCols, ...scoreCols];
            }

            allCols = [
                ...allCols,
                { key: 'totalScore', label: 'Total Score', width: 120 },
                { key: 'finalStatus', label: 'Final Status', width: 120 },
            ];
        }

        return allCols;
    }, [selectedForm, showQuestionScores]);

    // ===== GET CELL VALUE =====
    const getCellValue = (submission, columnKey) => {
        if (!selectedForm) return '';

        const isEvaluation = selectedForm.formType === 'evaluation';

        // Base columns
        if (columnKey === 'studentName') {
            return submission.studentName;
        }

        if (columnKey === 'submittedAt') {
            if (submission.submittedAt?.toDate) {
                return submission.submittedAt.toDate().toLocaleString();
            }
            return '-';
        }

        if (columnKey === 'status') {
            return (submission.status || 'submitted').toUpperCase();
        }

        // Individual question scores
        if (isEvaluation && columnKey.startsWith('score_')) {
            const qKey = columnKey.replace('score_', '');

            if (submission.questionScores && submission.questionScores[qKey]) {
                const score = submission.questionScores[qKey];
                // Format: "Obtained/Max (Percentage%)"
                return `${score.obtained}/${score.maxScore} (${score.percentage}%)`;
            }

            return '-';
        }

        // Total score
        if (isEvaluation && columnKey === 'totalScore') {
            if (submission.totalObtainedScore !== undefined && submission.totalMaxScore !== undefined) {
                return `${submission.totalObtainedScore}/${submission.totalMaxScore} (${submission.totalPercentage}%)`;
            }
            return '-';
        }

        // Final status
        if (isEvaluation && columnKey === 'finalStatus') {
            return submission.finalStatus || '-';
        }

        // Question responses
        const responses = submission.responses || {};
        let value = responses[columnKey];

        if (Array.isArray(value)) {
            value = value.length > 0 ? value : '';
        }

        return value ?? '';
    };


    // ===== EXPORT TO EXCEL =====
    const handleExportExcel = () => {
        if (!selectedForm || !filteredSubmissions.length) {
            alert('No submissions to export');
            return;
        }

        try {
            // Build header row
            const headerRow = columns.map((col) => col.label);

            // Build data rows
            const dataRows = filteredSubmissions.map((sub) => {
                const row = {};

                columns.forEach((col) => {
                    const value = getCellValue(sub, col.key);
                    row[col.label] = value;
                });

                return row;
            });

            // Create worksheet and workbook
            const worksheet = XLSX.utils.json_to_sheet(dataRows);

            // Set column widths
            const colWidths = columns.map((col) => ({ wch: col.width / 10 }));
            worksheet['!cols'] = colWidths;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Responses');

            // Generate filename
            const timestamp = new Date().toISOString().split('T');
            const fileName = `${selectedForm.title}_responses_${timestamp}.xlsx`;

            // Download
            XLSX.writeFile(workbook, fileName);
        } catch (err) {
            console.error('Error exporting to Excel:', err);
            alert('Failed to export to Excel');
        }
    };

    // ===== REFRESH DATA =====
    const handleRefresh = async () => {
        if (selectedFormId) {
            setLoading(true);
            try {
                const submissionsRef = collection(db, 'submissions');
                const q = query(
                    submissionsRef,
                    where('formId', '==', selectedFormId),
                    orderBy('submittedAt', 'desc')
                );

                const snap = await getDocs(q);
                const subsList = snap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setSubmissions(subsList);
                setFilteredSubmissions(subsList);
            } catch (err) {
                console.error('Error refreshing:', err);
            } finally {
                setLoading(false);
            }
        }
    };

    // ===== RENDER =====
    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                    📊 Scoreboard & Response Sheet
                </Typography>

                {/* Form Selector */}
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Select Form</InputLabel>
                                <Select
                                    value={selectedFormId}
                                    onChange={handleFormChange}
                                    label="Select Form"
                                >
                                    {forms.map((form) => (
                                        <MenuItem key={form.id} value={form.id}>
                                            {form.title} ({form.formType})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Date Filters */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={dateFromFilter}
                                onChange={(e) => setDateFromFilter(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={dateToFilter}
                                onChange={(e) => setDateToFilter(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                            />
                        </Grid>

                        {/* Evaluation Form Toggle */}
                        {selectedForm?.formType === 'evaluation' && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showQuestionScores}
                                            onChange={(e) => setShowQuestionScores(e.target.checked)}
                                        />
                                    }
                                    label="Show question-wise scores"
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={<ExportIcon />}
                        onClick={handleExportExcel}
                        disabled={!filteredSubmissions.length || loading}
                        sx={{ bgcolor: '#667eea' }}
                    >
                        Export to Excel
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        Refresh Data
                    </Button>

                    <Box sx={{ flex: 1 }} />

                    <Chip
                        label={`Total Submissions: ${filteredSubmissions.length}`}
                        color="primary"
                        variant="outlined"
                    />
                </Box>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Empty State */}
            {!loading && !filteredSubmissions.length && selectedForm && (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                    <Typography color="textSecondary">
                        No submissions found for the selected form and filters.
                    </Typography>
                </Paper>
            )}

            {/* Submissions Table */}
            {!loading && filteredSubmissions.length > 0 && (
                <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader>
                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            sx={{
                                                fontWeight: 'bold',
                                                color: '#333',
                                                bgcolor: '#f5f5f5',
                                                minWidth: col.width,
                                            }}
                                        >
                                            <Tooltip title={col.label} placement="top">
                                                <span>{col.label}</span>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSubmissions.map((submission, idx) => (
                                    <TableRow
                                        key={submission.id}
                                        hover
                                        sx={{
                                            backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                        }}
                                    >
                                        {columns.map((col) => {
                                            let cellValue = getCellValue(submission, col.key);
                                            let cellColor = 'inherit';

                                            // Style final status
                                            if (col.key === 'finalStatus') {
                                                if (cellValue === 'Admit') {
                                                    cellColor = '#4caf50';
                                                } else if (cellValue === 'Conditional') {
                                                    cellColor = '#ff9800';
                                                } else if (cellValue === 'Reject') {
                                                    cellColor = '#f44336';
                                                }
                                            }

                                            return (
                                                <TableCell
                                                    key={col.key}
                                                    sx={{
                                                        color: cellColor,
                                                        fontWeight: col.key === 'finalStatus' ? 600 : 400,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {cellValue}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Form Info */}
            {selectedForm && (
                <Paper sx={{ p: 2, mt: 3, bgcolor: '#e3f2fd' }}>
                    <Typography variant="body2">
                        <strong>Form:</strong> {selectedForm.title}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Type:</strong> {selectedForm.formType}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Questions/Fields:</strong>{' '}
                        {selectedForm.questions?.length ||
                            selectedForm.fields?.length ||
                            0}
                    </Typography>
                </Paper>
            )}
        </Container>
    );
}
